import React, { useState, useEffect } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Box, 
  Grid
} from '@mui/material';

// List of supported languages with their codes
// Using Google Cloud Translation API language codes
// https://cloud.google.com/translate/docs/languages
// 69 languages supported
const SUPPORTED_LANGUAGES = [
  // Major World Languages
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'ru', name: 'Russian' },
  { code: 'zh', name: 'Chinese (Simplified)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hi', name: 'Hindi' },
  { code: 'bn', name: 'Bengali' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'ur', name: 'Urdu' },
  { code: 'fa', name: 'Persian' },
  
  // European Languages
  { code: 'cs', name: 'Czech' },
  { code: 'pl', name: 'Polish' },
  { code: 'nl', name: 'Dutch' },
  { code: 'sv', name: 'Swedish' },
  { code: 'da', name: 'Danish' },
  { code: 'no', name: 'Norwegian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'el', name: 'Greek' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'ro', name: 'Romanian' },
  { code: 'hr', name: 'Croatian' },
  { code: 'sk', name: 'Slovak' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'et', name: 'Estonian' },
  { code: 'lv', name: 'Latvian' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'sr', name: 'Serbian' },
  { code: 'ca', name: 'Catalan' },
  { code: 'gl', name: 'Galician' },
  { code: 'eu', name: 'Basque' },
  { code: 'cy', name: 'Welsh' },
  { code: 'ga', name: 'Irish' },
  { code: 'is', name: 'Icelandic' },
  { code: 'mk', name: 'Macedonian' },
  { code: 'mt', name: 'Maltese' },
  { code: 'sq', name: 'Albanian' },
  
  // Middle Eastern & Central Asian
  { code: 'tr', name: 'Turkish' },
  { code: 'he', name: 'Hebrew' },
  { code: 'hy', name: 'Armenian' },
  { code: 'ka', name: 'Georgian' },
  { code: 'az', name: 'Azerbaijani' },
  { code: 'kk', name: 'Kazakh' },
  { code: 'uz', name: 'Uzbek' },
  
  // Southeast Asian Languages
  { code: 'th', name: 'Thai' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'id', name: 'Indonesian' },
  { code: 'ms', name: 'Malay' },
  { code: 'tl', name: 'Filipino (Tagalog)' },
  { code: 'my', name: 'Burmese' },
  { code: 'km', name: 'Khmer' },
  { code: 'lo', name: 'Lao' },
  { code: 'si', name: 'Sinhala' },
  { code: 'ne', name: 'Nepali' },
  
  // Other Practical Languages
  { code: 'af', name: 'Afrikaans' },
  { code: 'sw', name: 'Swahili' },
  { code: 'mn', name: 'Mongolian' },
];

// Source languages include auto-detect option
const SOURCE_LANGUAGES = [
  { code: 'auto', name: 'Auto-detect' },
  ...SUPPORTED_LANGUAGES
];

const LanguageSelector = () => {
  // Initialize state with values from localStorage or defaults
  // Default: Auto-detect source → English target
  const getInitialSourceLang = () => {
    const saved = localStorage.getItem('sourceLang');
    return saved || 'auto';
  };
  
  const getInitialTargetLang = () => {
    const saved = localStorage.getItem('targetLang');
    return saved || 'en';
  };
  
  const [sourceLang, setSourceLang] = useState(getInitialSourceLang());
  const [targetLang, setTargetLang] = useState(getInitialTargetLang());
  
  // Ensure defaults are set on first load
  useEffect(() => {
    if (!localStorage.getItem('sourceLang')) {
      localStorage.setItem('sourceLang', 'auto');
      console.log('Set default source language to: auto (Auto-detect)');
    }
    if (!localStorage.getItem('targetLang')) {
      localStorage.setItem('targetLang', 'en');
      console.log('Set default target language to: en (English)');
    }
  }, []);

  // Update localStorage when language selections change
  useEffect(() => {
    localStorage.setItem('sourceLang', sourceLang);
    console.log('Source language set to:', sourceLang);
  }, [sourceLang]);

  useEffect(() => {
    localStorage.setItem('targetLang', targetLang);
    console.log('Target language set to:', targetLang);
  }, [targetLang]);

  // Handle language selection changes
  const handleSourceLangChange = (event) => {
    const newSourceLang = event.target.value;
    setSourceLang(newSourceLang);
    
    // If source and target are the same (and source is not auto-detect), switch target to a different language
    if (newSourceLang === targetLang && newSourceLang !== 'auto') {
      const newTargetLang = newSourceLang === 'en' ? 'es' : 'en';
      setTargetLang(newTargetLang);
    }
  };

  const handleTargetLangChange = (event) => {
    const newTargetLang = event.target.value;
    setTargetLang(newTargetLang);
    
    // If source and target are the same (and source is not auto-detect), switch source to auto-detect
    if (sourceLang === newTargetLang && sourceLang !== 'auto') {
      setSourceLang('auto');
    }
  };

  return (
    <Box sx={{ mb: 1, mt: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel id="source-language-label">Source Language</InputLabel>
            <Select
              labelId="source-language-label"
              id="source-language"
              value={sourceLang}
              label="Source Language"
              onChange={handleSourceLangChange}
            >
              {SOURCE_LANGUAGES.map((lang) => (
                <MenuItem key={`source-${lang.code}`} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel id="target-language-label">Target Language</InputLabel>
            <Select
              labelId="target-language-label"
              id="target-language"
              value={targetLang}
              label="Target Language"
              onChange={handleTargetLangChange}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <MenuItem key={`target-${lang.code}`} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LanguageSelector;
